import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import "@/lonche.css";
import "@/admin.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["Clicked", "Paid", "Fulfilled", "Cancelled"];
const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

function LoginForm() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="adm-login-screen">
      <form className="adm-login" onSubmit={submit} data-testid="login-form">
        <span className="wm adm-login-mark" aria-label="Lonche">
          L<svg className="o" aria-hidden="true"><use href="#prawn" /></svg>NCHE
        </span>
        <p className="adm-login-title">Owner dashboard</p>
        <label className="adm-field">
          <span>Email</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username" data-testid="login-email" />
        </label>
        <label className="adm-field">
          <span>Password</span>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" data-testid="login-password" />
        </label>
        {error && <p className="adm-error" data-testid="login-error">{error}</p>}
        <button className="adm-btn" type="submit" disabled={busy} data-testid="login-submit">
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

function Dashboard() {
  const { user, logout, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [intents, setIntents] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (dateFilter) params.date = dateFilter;
      if (search.trim()) params.q = search.trim();
      const [statsRes, intentsRes] = await Promise.all([
        axios.get(`${API}/stats`, auth),
        axios.get(`${API}/intents`, { ...auth, params }),
      ]);
      setStats(statsRes.data);
      setIntents(intentsRes.data);
    } catch (e) {
      if (e.response?.status === 401) logout();
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, dateFilter, search, token]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/intents/${id}`, { status }, auth);
      setIntents((prev) => prev.map((it) => (it.id === id ? data : it)));
      // refresh stats counts
      axios.get(`${API}/stats`, auth).then((r) => setStats(r.data)).catch(() => {});
    } catch (e) {
      /* ignore */
    }
  };

  const clearFilters = () => {
    setStatusFilter("");
    setDateFilter("");
    setSearch("");
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
      });
    } catch (e) {
      return iso;
    }
  };

  return (
    <div className="adm-wrap" data-testid="admin-dashboard">
      <header className="adm-header">
        <span className="wm adm-mark" aria-label="Lonche">
          L<svg className="o" aria-hidden="true"><use href="#prawn" /></svg>NCHE
        </span>
        <div className="adm-header-right">
          <span className="adm-user">{user?.email}</span>
          <button className="adm-btn-ghost" onClick={logout} data-testid="logout-btn">Log out</button>
        </div>
      </header>

      <div className="adm-stats">
        <div className="adm-card" data-testid="stat-total-clicks">
          <span className="adm-card-label">Total clicks</span>
          <span className="adm-card-value">{stats ? stats.total_clicks : "—"}</span>
        </div>
        <div className="adm-card" data-testid="stat-today">
          <span className="adm-card-label">Clicks today</span>
          <span className="adm-card-value">{stats ? stats.clicks_today : "—"}</span>
        </div>
        <div className="adm-card" data-testid="stat-intended-value">
          <span className="adm-card-label">Intended value</span>
          <span className="adm-card-value">{stats ? inr(stats.total_intended_value) : "—"}</span>
        </div>
        <div className="adm-card adm-card-accent" data-testid="stat-paid-value">
          <span className="adm-card-label">Paid value</span>
          <span className="adm-card-value">{stats ? inr(stats.paid_value) : "—"}</span>
        </div>
      </div>

      <div className="adm-filters">
        <div className="adm-filter">
          <label>Status</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="filter-status">
            <option value="">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{s}{stats ? ` (${stats.counts[s] ?? 0})` : ""}</option>
            ))}
          </select>
        </div>
        <div className="adm-filter">
          <label>Date</label>
          <input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} data-testid="filter-date" />
        </div>
        <div className="adm-filter adm-filter-grow">
          <label>Search</label>
          <input type="text" placeholder="status, click id, pack…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="filter-search" />
        </div>
        <button className="adm-btn-ghost adm-clear" onClick={clearFilters} data-testid="filter-clear">Clear</button>
      </div>

      <div className="adm-table-wrap">
        <table className="adm-table" data-testid="intents-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Qty</th>
              <th>Pack</th>
              <th className="num">Total</th>
              <th>Visitor</th>
              <th>Source</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="adm-empty">Loading…</td></tr>
            )}
            {!loading && intents.length === 0 && (
              <tr><td colSpan={7} className="adm-empty" data-testid="intents-empty">No order intents yet.</td></tr>
            )}
            {!loading &&
              intents.map((it) => (
                <tr key={it.id} data-testid="intent-row">
                  <td className="adm-when">{fmt(it.timestamp)}</td>
                  <td>{it.quantity}</td>
                  <td>{it.pack}</td>
                  <td className="num">{inr(it.total)}</td>
                  <td className="adm-cid" title={it.click_id || ""}>{it.click_id ? it.click_id.slice(0, 8) : "—"}</td>
                  <td className="adm-ref" title={it.referrer || ""}>{it.referrer ? new URL(it.referrer).hostname : "direct"}</td>
                  <td>
                    <select
                      className={`adm-status adm-status-${it.status.toLowerCase()}`}
                      value={it.status}
                      onChange={(e) => updateStatus(it.id, e.target.value)}
                      data-testid={`status-select-${it.id}`}
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="adm-loading" data-testid="admin-loading">Loading…</div>;
  }
  return user ? <Dashboard /> : <LoginForm />;
}
